// src/components/sharedLists/SharedListPage.jsx
//
// /l/:code - a restaurant list shared from the YummiTracker app. With the
// app installed the link opens it (universal link); without, this page shows
// the list, a button to open it in the app, and the stores. A public list's
// approved photos (under each place, with the place's « Réponse de l'établissement ») and comments show too, read-only:
// posting, commenting and liking are in the app.
import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Paper, Stack, Chip, Button, CircularProgress, Alert, Link,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { sharedList, listComments, listPhotos, photoUrl, APP_LINK } from '../../utils/sharedListsApi';
import StoreButtons from './StoreButtons';

const osm = (lat, lon) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=18/${lat}/${lon}`;

const NETWORKS = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube', x: 'X', website: null };

/* The author's photo, bio and social links, once an admin approved them (backend utils/authorProfile.ts). */
function AuthorCard({ author }) {
  const { t } = useTranslation();
  const links = Object.entries(author.socials || {}).filter(([n]) => n in NETWORKS);
  if (!author.avatarUrl && !author.bio && !links.length) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#FBF8F2', borderColor: '#EADFCB' }} data-testid="author-card">
      <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
        {author.avatarUrl && (
          <Box component="img" src={author.avatarUrl} alt={author.name}
            sx={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '3px solid #C9A227', flexShrink: 0 }} />
        )}
        <Box sx={{ flex: '999 1 240px', minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800 }}>{t('sharedLists.by', { name: author.name })}</Typography>
          {author.bio && <Typography color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{author.bio}</Typography>}
          {links.length > 0 && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {links.map(([n, url]) => (
                <Button key={n} size="small" variant="outlined" href={url} target="_blank" rel="noopener noreferrer nofollow"
                  sx={{ borderRadius: 4, color: '#1C2B4A', borderColor: '#D9C3A0', textTransform: 'none', fontWeight: 600 }}>
                  {NETWORKS[n] ?? t('sharedLists.website')}
                </Button>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function SharedListPage() {
  const { code } = useParams();
  const { t, i18n } = useTranslation();
  const [list, setList] = useState(null);
  const [error, setError] = useState(false);
  const [comments, setComments] = useState(null); // { comments, count } of a public list
  const [photos, setPhotos] = useState([]); // its approved photos

  useEffect(() => {
    let alive = true;
    sharedList(String(code).toUpperCase())
      .then((l) => { if (alive) { setList(l); document.title = `${l.name} · YummiTracker`; } })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [code]);

  useEffect(() => {
    if (list?.status !== 'public') return undefined;
    let alive = true;
    Promise.resolve()
      .then(() => listComments(list.id))
      .then((r) => { if (alive && r) setComments(r); })
      .catch(() => {});
    Promise.resolve()
      .then(() => listPhotos(list.id))
      .then((r) => { if (alive && Array.isArray(r)) setPhotos(r.filter((p) => p.status === 'public')); })
      .catch(() => {});
    return () => { alive = false; };
  }, [list]);

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="info" sx={{ mb: 2 }}>{t('sharedLists.notFound')}</Alert>
        <Button component={RouterLink} to="/listes">{t('sharedLists.seeCharts')}</Button>
      </Container>
    );
  }
  if (!list) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="overline" color="text.secondary">{t('sharedLists.kicker')}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 800 }}>{list.name}</Typography>
      <Typography color="text.secondary" sx={{ mb: 1 }}>
        {t('sharedLists.by', { name: list.author.name })}{list.author.verified ? ' ✓' : ''}
        {list.city ? ` · ${list.city}` : ''} · {t('sharedLists.places', { count: list.itemCount })}
      </Typography>
      {list.description && <Typography sx={{ mb: 2 }}>{list.description}</Typography>}
      <AuthorCard author={list.author} />

      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#F4EEE3', borderColor: '#EADFCB' }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>{t('sharedLists.getApp')}</Typography>
        <Stack spacing={1}>
          <Button variant="contained" href={APP_LINK(list.shareCode)} sx={{ bgcolor: '#1C2B4A' }}>{t('sharedLists.openInApp')}</Button>
          <StoreButtons />
        </Stack>
      </Paper>

      <Stack spacing={1}>
        {list.items.map((p) => (
          <Paper key={p.position} variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 1.5 }}>
            <Typography sx={{ fontWeight: 800, width: 24, color: '#1C2B4A' }}>{p.position + 1}</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Link href={osm(p.lat, p.lon)} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>{p.name}</Link>
                {p.facility && <Chip size="small" label={`★ ${Number(p.facility.note).toFixed(1)} · ${t('sharedLists.verified')}`} />}
              </Stack>
              {p.address && <Typography variant="body2" color="text.secondary">{p.address}</Typography>}
              {p.note && <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>« {p.note} »</Typography>}
              {photos.some((x) => x.placeKey === p.placeKey) && (
                <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto' }}>
                  {photos.filter((x) => x.placeKey === p.placeKey).map((x) => (
                    <Link key={x.id} href={photoUrl(x.id)} target="_blank" rel="noopener noreferrer" sx={{ flex: '0 0 auto' }}>
                      <Box
                        component="img" src={photoUrl(x.id, true)} loading="lazy"
                        alt={t('sharedLists.photoAlt', { place: p.name, name: x.author.name })}
                        sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1.5, display: 'block' }}
                      />
                    </Link>
                  ))}
                </Stack>
              )}
              {photos.filter((x) => x.placeKey === p.placeKey && x.reply).map((x) => (
                <Box key={`reply-${x.id}`} sx={{ mt: 1, bgcolor: '#F4EEE3', borderLeft: '3px solid #C9A227', borderRadius: 1, p: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800 }}>{t('sharedLists.photoReply')} · {x.author.name}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{x.reply.body}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        ))}
      </Stack>

      {comments && (
        <Box component="section" sx={{ mt: 4 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 800 }}>
            {t('sharedLists.comments', { count: comments.count })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('sharedLists.commentInApp')}</Typography>
          <Stack spacing={1}>
            {comments.comments.map((c) => (
              <Paper key={c.id} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {c.author.name}{c.author.verified ? ' ✓' : ''}
                  {c.visitedPlaces > 0 && (
                    <Chip size="small" label={`✓ ${t('sharedLists.visitedPlaces', { count: c.visitedPlaces })}`}
                      sx={{ ml: 1, height: 20, fontSize: 11, fontWeight: 700, color: '#2F7D4F', bgcolor: '#E6F2EA' }} />
                  )}
                  <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary', ml: 1 }}>
                    {new Date(c.createdAt).toLocaleDateString(i18n.language)}
                  </Box>
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>{c.body}</Typography>
                {c.likes > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} aria-label={t('sharedLists.likes', { count: c.likes })}>
                    ♥ {c.likes}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      <Button component={RouterLink} to="/listes" sx={{ mt: 3 }}>{t('sharedLists.seeCharts')}</Button>
    </Container>
  );
}
